import { gql } from '@apollo/client';
import type { VisitorAssignment, VisitorExperimentSurface } from '@scio3/shared-types';

/**
 * OptiGrowth PUBLIC visitor-experiment surface — the two unauthenticated growth
 * mutations (spec §4). Both are `@Public` on open-api (no auth guard, POST-only)
 * and NEUTRAL on every guard trip: resolve returns `[]` (never an error) when
 * nothing is live / rate-limited / killed, and record always returns `true`.
 * The client must treat those neutral responses as "no experiments" — never as
 * a failure.
 *
 * Spec: claude-dev/kate/2026-07-21-optigrowth-visitor-experiments/ (harness root).
 */

/**
 * Resolve every live visitor-unit experiment on one pre-auth surface for one
 * og_vid. Returns ARM outcomes only (`{ key, arm }[]`) — NOT_ELIGIBLE /
 * NOT_ALLOCATED / unknown keys are invisible; an empty array means "nothing
 * live on this surface" and is a valid, cacheable result.
 */
export const RESOLVE_VISITOR_EXPERIMENTS = gql`
  mutation ResolveVisitorExperiments($input: ResolveVisitorExperimentsInput!) {
    resolveVisitorExperiments(input: $input) {
      key
      arm
    }
  }
`;

export interface ResolveVisitorExperimentsVariables {
  input: {
    surface: VisitorExperimentSurface;
    /** The durable browser id og_vid (`<uuid-v4>.<mintedAtMs>`). */
    visitorId: string;
  };
}

export interface ResolveVisitorExperimentsResponse {
  /** ARM outcomes only; `[]` when nothing is live/killed/over-limit. */
  resolveVisitorExperiments: VisitorAssignment[];
}

/**
 * Record a first-confirmed-render exposure for one (experiment, visitor) on a
 * surface. Fire-and-forget: the server always returns `true` (rate limit /
 * malformed key / unassigned visitor are indistinguishable), and the write is
 * de-duped server-side (write-once per experiment+visitor).
 */
export const RECORD_VISITOR_EXPOSURE = gql`
  mutation RecordVisitorExposure($input: RecordVisitorExposureInput!) {
    recordVisitorExposure(input: $input)
  }
`;

export interface RecordVisitorExposureVariables {
  input: {
    experimentKey: string;
    visitorId: string;
    surface: VisitorExperimentSurface;
  };
}

export interface RecordVisitorExposureResponse {
  /** Always `true` — fire-and-forget. */
  recordVisitorExposure: boolean;
}
